import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, PhoneIcon, CheckCircleIcon, AlertCircleIcon, ArrowLeftIcon, LockIcon, UserIcon, HomeIcon, HashIcon, CalendarIcon, DollarSignIcon, MapPinIcon, PlusIcon, TrashIcon, SaveIcon, FileTextIcon } from 'lucide-react';
interface BuyerLead {
  leadId: string;
  name: string;
  phone: string;
  propertyId?: string;
  fullName?: string;
  propertyType?: string;
  rooms?: number;
  budgetMin?: number;
  budgetMax?: number;
  zones: string[];
  paymentMethods?: string[];
  excludedFloors?: string;
  maxFloors?: number;
  bathrooms?: number;
  constructionYear?: number;
  qualificationMemo?: string;
  whoWillMove?: string;
  acquisitionReason?: string;
  leadSource?: string;
  entryDate?: string;
  entryTime?: string;
}
interface CallStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (status: string, qualificationData?: Partial<BuyerLead>) => void;
  onRescheduleCall?: (status: string) => void;
  onEditRequest?: () => void;
  leadName: string;
  currentLead?: BuyerLead;
}
const ROOM_OPTIONS = [1, 2, 3, 4, '4+'];
const BATHROOM_OPTIONS = [1, 2, 3, 4, '4+'];
const PROPERTY_TYPES = ['Apartament', 'Casă', 'Vilă', 'Penthouse', 'Studio'];
const callStatusOptions = [{
  value: 'nu-raspunde',
  label: 'Nu răspunde',
  icon: '📞',
  color: 'orange',
  description: 'Clientul nu a răspuns la apel - rămâne în undefinedDe apelatundefined (reapelabil)',
  needsQualification: false,
  isBlocking: false,
  action: 'stay-to-call'
}, {
  value: 'calificare',
  label: 'Calificare',
  icon: '✨',
  color: 'green',
  description: 'Clientul dorește închiriere - trece în undefinedCalificare", necesită completare criterii',
  needsQualification: true,
  isBlocking: false,
  action: 'qualify',
  requiresNoProperty: true
}, {
  value: 'vrea-vizionare',
  label: 'Vrea vizionare',
  icon: '✅',
  color: 'green',
  description: 'Clientul dorește vizionare - trece în "Apelate", necesită calificare',
  needsQualification: true,
  isBlocking: false,
  action: 'qualify',
  requiresProperty: true
}, {
  value: 'nu-vrea-vizionare',
  label: 'Nu vrea vizionare - Calificare',
  icon: '❌',
  color: 'gray',
  description: 'Clientul nu dorește vizionare - trece în "Apelate", necesită calificare',
  needsQualification: true,
  isBlocking: false,
  action: 'qualify',
  requiresProperty: true
}, {
  value: 'nu-colaboreaza',
  label: 'Nu colaborează',
  icon: '🔄',
  color: 'orange',
  description: 'Proprietarul refuză colaborarea - trece în "Apelate", redistribuie către alt agent',
  needsQualification: false,
  isBlocking: false,
  action: 'move-to-called'
}, {
  value: 'comision-redus',
  label: 'Oferă comision redus',
  icon: '💰',
  color: 'yellow',
  description: 'Comisionul oferit este sub standard - trece în "Apelate", de negociat',
  needsQualification: false,
  isBlocking: false,
  action: 'move-to-called'
}, {
  value: 'doreste-vanzare',
  label: 'Dorește vânzare',
  icon: '🏠',
  color: 'blue',
  description: 'Clientul caută vânzare - trece în "Apelate", redistribuie către modul Vânzare',
  needsQualification: false,
  isBlocking: false,
  action: 'transform'
}, {
  value: 'a-inchiriat',
  label: 'A închiriat deja',
  icon: '⛔',
  color: 'red',
  description: 'Proprietatea a fost deja închiriată - trece în "Apelate", LEAD BLOCAT',
  needsQualification: false,
  isBlocking: true,
  action: 'block'
}, {
  value: 'numar-invalid',
  label: 'Număr invalid',
  icon: '⛔',
  color: 'red',
  description: 'Numărul de telefon nu este valid - trece în "Apelate", LEAD BLOCAT',
  needsQualification: false,
  isBlocking: true,
  action: 'block'
}];
export function CallStatusModal({
  isOpen,
  onClose,
  onSubmit,
  onRescheduleCall,
  onEditRequest,
  leadName,
  currentLead
}: CallStatusModalProps) {
  const [step, setStep] = useState<'status' | 'qualification'>('status');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  // Form state - pre-populated with current lead data
  const [fullName, setFullName] = useState('');
  const [propertyType, setPropertyType] = useState(PROPERTY_TYPES[0]);
  const [selectedRooms, setSelectedRooms] = useState<(number | string)[]>([]);
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [surfaceMin, setSurfaceMin] = useState('');
  const [surfaceMax, setSurfaceMax] = useState('');
  const [zones, setZones] = useState<string[]>(['', '', '']);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [excludedFloors, setExcludedFloors] = useState('');
  const [maxFloors, setMaxFloors] = useState('');
  const [selectedBathrooms, setSelectedBathrooms] = useState<(number | string)[]>([]);
  const [constructionYear, setConstructionYear] = useState('');
  const [qualificationMemo, setQualificationMemo] = useState('');
  const [whoWillMove, setWhoWillMove] = useState('');
  const [acquisitionReason, setAcquisitionReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Pre-populate form with existing lead data when modal opens
  useEffect(() => {
    if (isOpen && currentLead) {
      setFullName(currentLead.fullName || currentLead.name || '');
      setPropertyType(currentLead.propertyType || PROPERTY_TYPES[0]);
      setSelectedRooms(currentLead.rooms ? [currentLead.rooms] : []);
      setBudgetMin(currentLead.budgetMin?.toString() || '');
      setBudgetMax(currentLead.budgetMax?.toString() || '');
      setSurfaceMin(currentLead.surfaceMin?.toString() || '');
      setSurfaceMax(currentLead.surfaceMax?.toString() || '');
      setZones(currentLead.zones?.length > 0 ? [...currentLead.zones, '', ''].slice(0, 5) : ['', '', '']);
      setPaymentMethods(currentLead.paymentMethods || []);
      setExcludedFloors(currentLead.excludedFloors || '');
      setMaxFloors(currentLead.maxFloors?.toString() || '');
      setSelectedBathrooms(currentLead.bathrooms ? [currentLead.bathrooms] : []);
      setConstructionYear(currentLead.constructionYear?.toString() || '');
      setQualificationMemo(currentLead.qualificationMemo || '');
      setWhoWillMove(currentLead.whoWillMove || '');
      setAcquisitionReason(currentLead.acquisitionReason || '');
    }
  }, [isOpen, currentLead]);
  const availableStatusOptions = callStatusOptions.filter(option => {
    if (option.requiresNoProperty) {
      return !currentLead?.propertyId;
    }
    if (option.requiresProperty) {
      return !!currentLead?.propertyId;
    }
    return true;
  });
  const handleStatusSelect = (status: string) => {
    setSelectedStatus(status);
    const option = callStatusOptions.find(o => o.value === status);
    if (option?.needsQualification) {
      setStep('qualification');
    }
  };
  const handleRoomToggle = (room: number | string) => {
    if (selectedRooms.includes(room)) {
      setSelectedRooms(selectedRooms.filter(r => r !== room));
    } else {
      setSelectedRooms([...selectedRooms, room]);
    }
  };
  const handleBathroomToggle = (bathroom: number | string) => {
    if (selectedBathrooms.includes(bathroom)) {
      setSelectedBathrooms(selectedBathrooms.filter(b => b !== bathroom));
    } else {
      setSelectedBathrooms([...selectedBathrooms, bathroom]);
    }
  };
  const handlePaymentMethodToggle = (method: string) => {
    if (paymentMethods.includes(method)) {
      setPaymentMethods(paymentMethods.filter(m => m !== method));
    } else {
      setPaymentMethods([...paymentMethods, method]);
    }
  };
  const handleZoneChange = (index: number, value: string) => {
    const newZones = [...zones];
    newZones[index] = value;
    setZones(newZones);
  };
  const addZone = () => {
    if (zones.length < 5) {
      setZones([...zones, '']);
    }
  };
  const removeZone = (index: number) => {
    if (zones.length > 3) {
      const newZones = zones.filter((_, i) => i !== index);
      setZones(newZones);
    }
  };
  const validateQualification = () => {
    const newErrors: Record<string, string> = {};
    if (!fullName.trim()) newErrors.fullName = 'Numele este obligatoriu';
    if (!budgetMin) newErrors.budgetMin = 'Bugetul minim este obligatoriu';
    if (!budgetMax) newErrors.budgetMax = 'Bugetul maxim este obligatoriu';
    if (budgetMin && budgetMax && parseFloat(budgetMax) < parseFloat(budgetMin)) {
      newErrors.budgetMax = 'Bugetul maxim trebuie să fie mai mare sau egal cu minimul';
    }
    if (!surfaceMin) newErrors.surfaceMin = 'Suprafața minimă este obligatorie';
    if (!surfaceMax) newErrors.surfaceMax = 'Suprafața maximă este obligatorie';
    if (surfaceMin && surfaceMax && parseFloat(surfaceMax) < parseFloat(surfaceMin)) {
      newErrors.surfaceMax = 'Suprafața maximă trebuie să fie mai mare sau egală cu minima';
    }
    if (selectedRooms.length === 0) newErrors.rooms = 'Selectează cel puțin o opțiune';
    const filledZones = zones.filter(z => z.trim().length > 0);
    if (filledZones.length < 3) {
      newErrors.zones = 'Minim 3 zone sunt obligatorii';
    }
    if (!whoWillMove.trim()) {
      newErrors.whoWillMove = 'Acest câmp este obligatoriu';
    }
    if (!acquisitionReason.trim()) {
      newErrors.acquisitionReason = 'Acest câmp este obligatoriu';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (!selectedStatus) {
      alert('Te rog selectează un status pentru apel');
      return;
    }
    const option = callStatusOptions.find(o => o.value === selectedStatus);
    if (option?.action === 'stay-to-call' || option?.action === 'move-to-called' || option?.action === 'block' || option?.action === 'transform') {
      onSubmit(selectedStatus);
      handleCloseModal();
      return;
    }
    if (option?.action === 'qualify' && step === 'qualification') {
      if (!validateQualification()) return;
      const qualificationData: Partial<BuyerLead> = {
        fullName,
        propertyType,
        rooms: typeof selectedRooms[0] === 'number' ? selectedRooms[0] : parseInt(selectedRooms[0] as string) || 1,
        budgetMin: parseInt(budgetMin),
        budgetMax: parseInt(budgetMax),
        surfaceMin: parseInt(surfaceMin),
        surfaceMax: parseInt(surfaceMax),
        zones: zones.filter(z => z.trim()),
        paymentMethods: paymentMethods.length > 0 ? paymentMethods : undefined,
        excludedFloors: excludedFloors.trim() || undefined,
        maxFloors: maxFloors ? parseInt(maxFloors) : undefined,
        bathrooms: selectedBathrooms.length > 0 ? typeof selectedBathrooms[0] === 'number' ? selectedBathrooms[0] : parseInt(selectedBathrooms[0] as string) || undefined : undefined,
        constructionYear: constructionYear ? parseInt(constructionYear) : undefined,
        qualificationMemo: qualificationMemo.trim() || undefined,
        whoWillMove: whoWillMove.trim() || undefined,
        acquisitionReason: acquisitionReason.trim() || undefined
      };
      onSubmit(selectedStatus, qualificationData);
      handleCloseModal();
      return;
    }
    onSubmit(selectedStatus);
    handleCloseModal();
  };
  const handleBack = () => {
    setStep('status');
  };
  const handleCloseModal = () => {
    setStep('status');
    setSelectedStatus('');
    setErrors({});
    onClose();
  };
  const getColorClasses = (color: string, isSelected: boolean, isBlocking: boolean) => {
    const baseClasses = 'border-2 rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg';
    if (isBlocking) {
      return isSelected ? `${baseClasses} border-red-600 bg-red-50 shadow-md ring-2 ring-red-300` : `${baseClasses} border-red-300 bg-red-50/50 hover:border-red-400`;
    }
    if (isSelected) {
      switch (color) {
        case 'orange':
          return `${baseClasses} border-orange-500 bg-orange-50 shadow-md`;
        case 'red':
          return `${baseClasses} border-red-500 bg-red-50 shadow-md`;
        case 'green':
          return `${baseClasses} border-green-500 bg-green-50 shadow-md`;
        case 'blue':
          return `${baseClasses} border-blue-500 bg-blue-50 shadow-md`;
        case 'gray':
          return `${baseClasses} border-gray-500 bg-gray-50 shadow-md`;
        default:
          return `${baseClasses} border-blue-500 bg-blue-50 shadow-md`;
      }
    }
    return `${baseClasses} border-gray-200 hover:border-gray-300`;
  };
  const getActionButtonText = () => {
    const option = callStatusOptions.find(o => o.value === selectedStatus);
    if (!option) return 'Confirmă Status';
    switch (option.action) {
      case 'stay-to-call':
        return 'Finalizează Apel';
      case 'transform':
        return 'Transformă Lead →';
      case 'move-to-called':
        return 'Finalizează Apel';
      case 'block':
        return 'Blochează Lead';
      case 'qualify':
        return step === 'qualification' ? 'Salvează Calificare' : 'Continuă →';
      default:
        return 'Confirmă Status';
    }
  };
  return <AnimatePresence data-id="element-2727">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleCloseModal} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-200" style={{
        zIndex: 200
      }} data-id="element-2728" />

          {/* Sidebar Panel - matching NewLeadModal design */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed right-0 top-16 bottom-0 w-full sm:w-[650px] bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 shadow-2xl z-210 overflow-y-auto" style={{
        zIndex: 210
      }} data-id="element-2729">
            {/* Header */}
            <div className={`text-white px-6 py-5 shadow-lg ${step === 'qualification' ? 'bg-gradient-to-br from-[#21A366] to-[#1a8555]' : 'bg-gradient-to-br from-purple-500 to-purple-600'}`} data-id="element-2730">
              <div className="flex items-center justify-between" data-id="element-2731">
                <div data-id="element-2732">
                  <div className="flex items-center space-x-2 mb-2" data-id="element-2733">
                    {step === 'qualification' && <button onClick={handleBack} className="p-2 hover:bg-white/20 rounded-xl transition-colors mr-2" data-id="element-2734">
                        <ArrowLeftIcon className="w-5 h-5" data-id="element-2735" />
                      </button>}
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-2736">
                      {step === 'qualification' ? <CheckCircleIcon className="w-5 h-5 text-white" data-id="element-2737" /> : <PhoneIcon className="w-5 h-5 text-white" data-id="element-2738" />}
                    </div>
                    <div data-id="element-2739">
                      <h3 className="text-xl font-bold" data-id="element-2740">
                        {step === 'qualification' ? 'Formular Calificare' : 'Rezultat Apel'}
                      </h3>
                      <p className={`text-sm ${step === 'qualification' ? 'text-green-100' : 'text-purple-100'}`} data-id="element-2741">
                        {step === 'qualification' ? `Completează criteriile pentru ${leadName}` : `Selectează statusul apelului pentru ${leadName}`}
                      </p>
                    </div>
                  </div>
                </div>
                <button onClick={handleCloseModal} className="p-2 hover:bg-white/20 rounded-xl transition-colors backdrop-blur-sm" data-id="element-2742">
                  <XIcon className="w-6 h-6 text-white" data-id="element-2743" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6" data-id="element-2744">
              {step === 'status' ? <>
                  {/* Lead Info Card */}
                  {currentLead && <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2745">
                      <div className="flex items-center space-x-3 mb-4" data-id="element-2746">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center" data-id="element-2747">
                          <HashIcon className="w-6 h-6 text-white" data-id="element-2748" />
                        </div>
                        <div data-id="element-2749">
                          <span className="text-lg font-bold text-[#132C53]" data-id="element-2750">
                            {currentLead.leadId}
                          </span>
                          <h4 className="text-xl font-bold text-[#132C53]" data-id="element-2751">
                            {leadName}
                          </h4>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2752">
                        <div className="flex items-center space-x-2" data-id="element-2753">
                          <PhoneIcon className="w-4 h-4 text-purple-500" data-id="element-2754" />
                          <span className="text-slate-600" data-id="element-2755">
                            {currentLead.phone}
                          </span>
                        </div>
                        {currentLead.entryDate && <div className="flex items-center space-x-2" data-id="element-2756">
                            <CalendarIcon className="w-4 h-4 text-blue-500" data-id="element-2757" />
                            <span className="text-slate-600" data-id="element-2758">
                              {currentLead.entryDate}
                            </span>
                          </div>}
                      </div>
                    </motion.div>}

                  {/* Info Alert */}
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-5 shadow-lg border border-slate-200" data-id="element-2759">
                    <div className="flex items-start space-x-3" data-id="element-2760">
                      <AlertCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-2761" />
                      <div data-id="element-2762">
                        <p className="text-sm font-semibold text-blue-900 mb-1" data-id="element-2763">
                          Indexare obligatorie
                        </p>
                        <p className="text-sm text-blue-700" data-id="element-2764">
                          Selectează rezultatul conversației. Unele statusuri
                          blochează lead-ul permanent (⛔).
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Status Options */}
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2765">
                    <h5 className="text-lg font-bold text-[#132C53] mb-4" data-id="element-2766">
                      Selectează Status
                    </h5>
                    <div className="grid grid-cols-1 gap-3" data-id="element-2767">
                      {availableStatusOptions.map(option => <motion.div key={option.value} whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => handleStatusSelect(option.value)} className={getColorClasses(option.color, selectedStatus === option.value, option.isBlocking)} data-id="element-2768">
                          <div className="flex items-start space-x-3" data-id="element-2769">
                            <div className="text-2xl flex-shrink-0" data-id="element-2770">
                              {option.icon}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-2771">
                              <div className="flex items-center space-x-2 mb-1" data-id="element-2772">
                                <h4 className="font-bold text-gray-900 text-sm" data-id="element-2773">
                                  {option.label}
                                </h4>
                                {option.isBlocking && <LockIcon className="w-4 h-4 text-red-600" data-id="element-2774" />}
                              </div>
                              <p className="text-xs text-gray-600 leading-relaxed" data-id="element-2775">
                                {option.description}
                              </p>
                            </div>
                            {selectedStatus === option.value && <motion.div initial={{
                      scale: 0
                    }} animate={{
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 30
                    }} data-id="element-2776">
                                <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-2777" />
                              </motion.div>}
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>
                </> : <>
                  {/* Qualification Form - matching NewLeadModal design */}
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2778">
                    <div className="flex items-start space-x-3 mb-4" data-id="element-2779">
                      <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-2780" />
                      <div data-id="element-2781">
                        <p className="text-sm font-semibold text-green-900 mb-1" data-id="element-2782">
                          Status:{' '}
                          {callStatusOptions.find(o => o.value === selectedStatus)?.label}
                        </p>
                        <p className="text-sm text-green-700" data-id="element-2783">
                          Completează criteriile de calificare pentru a finaliza
                          procesarea leadului.
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Contact Info Card */}
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2784">
                    <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-2785">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-2786">
                        <UserIcon className="w-5 h-5 text-blue-600" data-id="element-2787" />
                      </div>
                      <span data-id="element-2788">Date Contact</span>
                    </h5>

                    <div className="space-y-4" data-id="element-2789">
                      <div data-id="element-2790">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2791">
                          Nume Complet <span className="text-red-500" data-id="element-2792">*</span>
                        </label>
                        <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 outline-none transition-all ${errors.fullName ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder="ex: Popescu Ion" data-id="element-2793" />
                        {errors.fullName && <p className="text-xs text-red-500 mt-1 flex items-center space-x-1" data-id="element-2794">
                            <span data-id="element-2795">⚠️</span>
                            <span data-id="element-2796">{errors.fullName}</span>
                          </p>}
                      </div>

                      <div data-id="element-2797">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2798">
                          Telefon <span className="text-red-500" data-id="element-2799">*</span>
                        </label>
                        <div className="relative" data-id="element-2800">
                          <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2801" />
                          <input type="tel" value={currentLead?.phone || ''} readOnly className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-600 font-mono" data-id="element-2802" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2803">
                        <div data-id="element-2804">
                          <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2805">
                            Sursa Lead
                          </label>
                          <input type="text" value={currentLead?.leadSource || 'ID proprietate'} readOnly className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-600" data-id="element-2806" />
                        </div>

                        {currentLead?.propertyId && <div data-id="element-2807">
                            <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2808">
                              ID Proprietate{' '}
                              <span className="text-red-500" data-id="element-2809">*</span>
                            </label>
                            <input type="text" value={currentLead.propertyId} readOnly className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-600 font-mono" data-id="element-2810" />
                          </div>}
                      </div>
                    </div>
                  </motion.div>

                  {/* Requirements Card */}
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2811">
                    <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-2812">
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center" data-id="element-2813">
                        <HomeIcon className="w-5 h-5 text-emerald-600" data-id="element-2814" />
                      </div>
                      <span data-id="element-2815">Cerințe Imobiliare</span>
                    </h5>

                    <div className="space-y-4" data-id="element-2816">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2817">
                        <div data-id="element-2818">
                          <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2819">
                            Tip Imobil
                          </label>
                          <select value={propertyType} onChange={e => setPropertyType(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none bg-white" data-id="element-2820">
                            {PROPERTY_TYPES.map(type => <option key={type} value={type} data-id="element-2821">
                                {type}
                              </option>)}
                          </select>
                        </div>

                        <div data-id="element-2822">
                          <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2823">
                            Nr. Camere <span className="text-red-500" data-id="element-2824">*</span>
                          </label>
                          <div className="flex flex-wrap gap-2" data-id="element-2825">
                            {ROOM_OPTIONS.map(opt => <button key={opt} type="button" onClick={() => handleRoomToggle(opt)} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${selectedRooms.includes(opt) ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`} data-id="element-2826">
                                {opt}
                              </button>)}
                          </div>
                          {errors.rooms && <p className="text-xs text-red-500 mt-2 flex items-center space-x-1" data-id="element-2827">
                              <span data-id="element-2828">⚠️</span>
                              <span data-id="element-2829">{errors.rooms}</span>
                            </p>}
                        </div>
                      </div>

                      <div data-id="element-2830">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2831">
                          Buget (EUR) <span className="text-red-500" data-id="element-2832">*</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3" data-id="element-2833">
                          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2834">
                            <label className="block text-xs text-slate-500 mb-2 font-medium" data-id="element-2835">
                              Minim
                            </label>
                            <div className="relative" data-id="element-2836">
                              <DollarSignIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2837" />
                              <input type="number" value={budgetMin} onChange={e => setBudgetMin(e.target.value)} className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-white ${errors.budgetMin ? 'border-red-300' : 'border-slate-200'}`} placeholder="400" data-id="element-2838" />
                            </div>
                            {errors.budgetMin && <p className="text-xs text-red-500 mt-1" data-id="element-2839">
                                {errors.budgetMin}
                              </p>}
                          </div>
                          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200" data-id="element-2840">
                            <label className="block text-xs text-emerald-600 mb-2 font-medium" data-id="element-2841">
                              Maxim
                            </label>
                            <div className="relative" data-id="element-2842">
                              <DollarSignIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" data-id="element-2843" />
                              <input type="number" value={budgetMax} onChange={e => setBudgetMax(e.target.value)} className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-white ${errors.budgetMax ? 'border-red-300' : 'border-emerald-200'}`} placeholder="500" data-id="element-2844" />
                            </div>
                            {errors.budgetMax && <p className="text-xs text-red-500 mt-1" data-id="element-2845">
                                {errors.budgetMax}
                              </p>}
                          </div>
                        </div>
                      </div>

                      <div data-id="element-2846">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2847">
                          Suprafață (m²) <span className="text-red-500" data-id="element-2848">*</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3" data-id="element-2849">
                          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2850">
                            <label className="block text-xs text-slate-500 mb-2 font-medium" data-id="element-2851">
                              Minim
                            </label>
                            <div className="relative" data-id="element-2852">
                              <HomeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2853" />
                              <input type="number" value={surfaceMin} onChange={e => setSurfaceMin(e.target.value)} className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 outline-none bg-white ${errors.surfaceMin ? 'border-red-300' : 'border-slate-200'}`} placeholder="50" data-id="element-2854" />
                            </div>
                            {errors.surfaceMin && <p className="text-xs text-red-500 mt-1" data-id="element-2855">
                                {errors.surfaceMin}
                              </p>}
                          </div>
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200" data-id="element-2856">
                            <label className="block text-xs text-blue-600 mb-2 font-medium" data-id="element-2857">
                              Maxim
                            </label>
                            <div className="relative" data-id="element-2858">
                              <HomeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" data-id="element-2859" />
                              <input type="number" value={surfaceMax} onChange={e => setSurfaceMax(e.target.value)} className={`w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white ${errors.surfaceMax ? 'border-red-300' : 'border-blue-200'}`} placeholder="75" data-id="element-2860" />
                            </div>
                            {errors.surfaceMax && <p className="text-xs text-red-500 mt-1" data-id="element-2861">
                                {errors.surfaceMax}
                              </p>}
                          </div>
                        </div>
                      </div>

                      <div data-id="element-2862">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2863">
                          Modalitate de Plată
                        </label>
                        <div className="flex flex-wrap gap-3" data-id="element-2864">
                          <button type="button" onClick={() => handlePaymentMethodToggle('cash')} className={`flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-bold transition-all border ${paymentMethods.includes('cash') ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`} data-id="element-2865">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${paymentMethods.includes('cash') ? 'border-white bg-white' : 'border-slate-300'}`} data-id="element-2866">
                              {paymentMethods.includes('cash') && <CheckCircleIcon className="w-4 h-4 text-emerald-600" data-id="element-2867" />}
                            </div>
                            <span data-id="element-2868">Cash</span>
                          </button>
                          <button type="button" onClick={() => handlePaymentMethodToggle('credit')} className={`flex items-center space-x-2 px-4 py-3 rounded-xl text-sm font-bold transition-all border ${paymentMethods.includes('credit') ? 'bg-emerald-600 text-white border-emerald-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'}`} data-id="element-2869">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${paymentMethods.includes('credit') ? 'border-white bg-white' : 'border-slate-300'}`} data-id="element-2870">
                              {paymentMethods.includes('credit') && <CheckCircleIcon className="w-4 h-4 text-emerald-600" data-id="element-2871" />}
                            </div>
                            <span data-id="element-2872">Credit</span>
                          </button>
                        </div>
                      </div>

                      <div data-id="element-2873">
                        <div className="flex items-center justify-between mb-2" data-id="element-2874">
                          <label className="block text-xs font-medium text-slate-500" data-id="element-2875">
                            Zone Preferate (minim 3){' '}
                            <span className="text-red-500" data-id="element-2876">*</span>
                          </label>
                          {zones.length < 5 && <button onClick={addZone} className="text-xs flex items-center gap-1 text-emerald-600 font-bold hover:bg-emerald-50 px-2 py-1 rounded-lg transition-colors" data-id="element-2877">
                              <PlusIcon className="w-3 h-3" data-id="element-2878" /> Adaugă Zonă
                            </button>}
                        </div>

                        <div className="space-y-2" data-id="element-2879">
                          {zones.map((zone, idx) => <div key={idx} className="flex gap-2" data-id="element-2880">
                              <div className="relative flex-1" data-id="element-2881">
                                <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-2882" />
                                <input type="text" value={zone} onChange={e => handleZoneChange(idx, e.target.value)} className={`w-full pl-10 pr-3 py-3 border rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none ${errors.zones && !zone.trim() ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`} placeholder={`Zona ${idx + 1}`} data-id="element-2883" />
                              </div>
                              {zones.length > 3 && <button onClick={() => removeZone(idx)} className="p-3 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors" data-id="element-2884">
                                  <TrashIcon className="w-4 h-4" data-id="element-2885" />
                                </button>}
                            </div>)}
                        </div>
                        {errors.zones && <p className="text-xs text-red-500 mt-2 flex items-center space-x-1" data-id="element-2886">
                            <span data-id="element-2887">⚠️</span>
                            <span data-id="element-2888">{errors.zones}</span>
                          </p>}
                      </div>
                    </div>
                  </motion.div>

                  {/* Optional Fields Card */}
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2889">
                    <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-2890">
                      <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center" data-id="element-2891">
                        <HomeIcon className="w-5 h-5 text-slate-600" data-id="element-2892" />
                      </div>
                      <span data-id="element-2893">Câmpuri Opționale</span>
                    </h5>

                    <div className="space-y-4" data-id="element-2894">
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2895">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2896">
                          Etaje Excluse
                        </label>
                        <input type="text" value={excludedFloors} onChange={e => setExcludedFloors(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white" placeholder="ex: parter, etaj 1" data-id="element-2897" />
                      </div>

                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2898">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2899">
                          Nr. Maxim de Etaje
                        </label>
                        <input type="number" value={maxFloors} onChange={e => setMaxFloors(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white" placeholder="ex: 5" data-id="element-2900" />
                      </div>

                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2901">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2902">
                          Nr. Băi
                        </label>
                        <div className="flex flex-wrap gap-2" data-id="element-2903">
                          {BATHROOM_OPTIONS.map(opt => <button key={opt} type="button" onClick={() => handleBathroomToggle(opt)} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${selectedBathrooms.includes(opt) ? 'bg-slate-600 text-white border-slate-600 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-id="element-2904">
                              {opt}
                            </button>)}
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2905">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2906">
                          An Construcție
                        </label>
                        <input type="number" value={constructionYear} onChange={e => setConstructionYear(e.target.value)} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-500 outline-none bg-white" placeholder="ex: 2015" data-id="element-2907" />
                      </div>
                    </div>
                  </motion.div>

                  {/* Qualification Questions Card */}
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2908">
                    <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-2909">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-2910">
                        <CheckCircleIcon className="w-5 h-5 text-purple-600" data-id="element-2911" />
                      </div>
                      <span data-id="element-2912">Întrebări Calificare</span>
                    </h5>

                    <div className="space-y-4" data-id="element-2913">
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2914">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2915">
                          Cine se va muta în noul imobil?{' '}
                          <span className="text-red-500" data-id="element-2916">*</span>
                        </label>
                        <input type="text" value={whoWillMove} onChange={e => setWhoWillMove(e.target.value)} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white ${errors.whoWillMove ? 'border-red-300' : 'border-slate-200'}`} placeholder="ex: Familie cu 2 copii, Persoană singură, Cuplu tânăr..." data-id="element-2917" />
                        {errors.whoWillMove && <p className="text-xs text-red-500 mt-1 flex items-center space-x-1" data-id="element-2918">
                            <span data-id="element-2919">⚠️</span>
                            <span data-id="element-2920">{errors.whoWillMove}</span>
                          </p>}
                      </div>

                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2921">
                        <label className="block text-xs font-medium text-slate-500 mb-2" data-id="element-2922">
                          De ce se dorește o altă achiziție de imobil?{' '}
                          <span className="text-red-500" data-id="element-2923">*</span>
                        </label>
                        <textarea value={acquisitionReason} onChange={e => setAcquisitionReason(e.target.value)} rows={3} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 outline-none resize-none bg-white ${errors.acquisitionReason ? 'border-red-300' : 'border-slate-200'}`} placeholder="ex: Mutare în alt oraș, Spațiu mai mare, Aproape de serviciu..." data-id="element-2924" />
                        {errors.acquisitionReason && <p className="text-xs text-red-500 mt-1 flex items-center space-x-1" data-id="element-2925">
                            <span data-id="element-2926">⚠️</span>
                            <span data-id="element-2927">{errors.acquisitionReason}</span>
                          </p>}
                      </div>
                    </div>
                  </motion.div>

                  {/* Memo Card */}
                  <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200" data-id="element-2928">
                    <h5 className="text-lg font-bold text-[#132C53] mb-5 flex items-center space-x-2" data-id="element-2929">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-2930">
                        <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-2931" />
                      </div>
                      <span data-id="element-2932">Memo Calificare</span>
                    </h5>
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200" data-id="element-2933">
                      <textarea value={qualificationMemo} onChange={e => setQualificationMemo(e.target.value)} rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none bg-white" placeholder="Alte detalii relevante despre client..." data-id="element-2934" />
                    </div>
                  </motion.div>
                </>}

              {/* Action Buttons */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex space-x-3 pb-6" data-id="element-2935">
                <button onClick={handleCloseModal} className="flex-1 px-6 py-3 rounded-xl font-bold text-slate-600 bg-white border-2 border-slate-200 hover:bg-slate-50 transition-colors" data-id="element-2936">
                  Anulează
                </button>
                <button onClick={handleSubmit} disabled={!selectedStatus} className={`flex-1 px-6 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${selectedStatus ? step === 'qualification' ? 'bg-gradient-to-r from-[#21A366] to-[#1a8555] text-white hover:shadow-lg hover:scale-105' : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:shadow-lg hover:scale-105' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2937">
                  <SaveIcon className="w-5 h-5" data-id="element-2938" />
                  {getActionButtonText()}
                </button>
              </motion.div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}